// messages.mjs — 站内信页的纯逻辑（第一百零九批 D）。无 React、无 DOM。
// 房屋风格同 pages/threads.mjs / reports.mjs：可判定的东西离开 JSX 才有判据（SB-042）。
//
// 这一页的三条承重规则，决定了下面每只函数的形状：
//
// 1) **列表面绝不出现正文或摘要**（需求方裁定 3 的直接后果）。「点开正文即已读」把
//    "列表不许有正文"从一条纪律升级成了判据的前提：他在列表里读到了、而系统说未读，
//    撤回边界与防 spam 判据同时失真。后端的 `DirectMessageRow` 里根本没有 body 字段，
//    所以 normalize 这一层**也不留任何正文形状的字段**——一个 `bodyPreview` 的空位就是
//    下一个人往里填东西的邀请。判据在 messages.test.mjs 里逐字段咬住。
//
// 2) 状态与「能不能撤回」**一处派生**。后端的 `dm_state` 是唯一的裁决者，行上带着
//    `state` 与 `mayWithdraw`；这里**只做形状收敛，不重算判据**。前端再算一遍的结局
//    密递头注已经写死了：清单上写着"可撤回"、点下去回 409，而两边都"没错"。
//
// 3) 三族动态键（state / anchor / failReason）必须有**显式的字面量映射表**。
//    i18n/keys.test.mjs 扫的是源码里写死的键（单引号字面量），动态拼出来的键它看不见——
//    于是漏一个键会躲过全部判据、以自己的点号名字渲染给用户（第五十四批 COMMON.CLOSE
//    那次付过账）。表在这里，配一条"表里每个键在 zh/en 两侧都有落地文案"的单测。
//
// ── fail-closed 的方向 ──────────────────────────────────────────────────────
// 未知的 state / anchorKind / failReason（服务端先上线了新值）一律回落到 `null` 键，
// 调用侧渲染原始串而不是一个猜出来的标签。猜一个标签的代价是"用户看到一句确定的
// 假话"，而看到 `sent_unread` 这个原始串至少能被报上来。

import { normalizeResponseDeadline } from './responseSla.mjs';

// 正文上限。与 defaults.h 的 kDmBodyMaxChars 同值；契约 C.1 与 dm.new.tooLong 的
// 那个 20000 说的是同一个数（后端仍是唯一的闸，这里只为在他打完之前告诉他）。
export const DM_BODY_MAX = 20000;
// 延迟发送窗口。defaults.h 的 kDmScheduleMaxMs = 72 小时；"最多能等多久"与"最多能
// 排到多久以后"是同一件事的两半，所以本仓只有这一个数（契约 B.4c）。
export const DM_SCHEDULE_MAX_MS = 72 * 3600 * 1000;

// ── 三族显式映射表（头注 3）────────────────────────────────────────────────
export const DM_STATE_LABEL = {
  queued: 'dm.state.queued',
  sent_unread: 'dm.state.sent_unread',
  read: 'dm.state.read',
  withdrawn: 'dm.state.withdrawn',
  failed: 'dm.state.failed',
};

// 徽标配色。`failed` 用 error 而不是 default：一封没发出去的信是要人动手重写的，
// 而灰色徽标在一列灰色里等于不存在（SB-120：失败被呈现成什么都没有）。
export const DM_STATE_COLOR = {
  queued: 'default',
  sent_unread: 'info',
  read: 'success',
  withdrawn: 'warning',
  failed: 'error',
};

export const DM_ANCHOR_LABEL = {
  same_group: 'dm.anchor.same_group',
  work_item: 'dm.anchor.work_item',
  doc: 'dm.anchor.doc',
  reply: 'dm.anchor.reply',
};

export const DM_FAIL_REASON_LABEL = {
  wait_expired: 'dm.failReason.wait_expired',
  worker_gave_up: 'dm.failReason.worker_gave_up',
};

// ── 错误码 → i18n 键（errText 的 byCode 表，契约 D.7）────────────────────────
// 发信与撤回共用一张表：两张表意味着同一个码在两个面上说两句话，而用户不知道
// 自己看的是哪一面。未映射的码由 errKey 回落到 fallback（原文永不直接打给用户）。
export const DM_ERROR_KEYS = {
  ANCHOR_REQUIRED: 'dm.err.anchorRequired',
  ANCHOR_UNKNOWN_KIND: 'dm.err.anchorKind',
  ANCHOR_NOT_SHARED: 'dm.err.anchorNotShared',
  DM_ALREADY_QUEUED: 'dm.err.alreadyQueued',
  ALREADY_READ: 'dm.err.alreadyRead',
  ALREADY_WITHDRAWN: 'dm.err.alreadyWithdrawn',
  ALREADY_FAILED: 'dm.err.alreadyFailed',
  GONE_WITHDRAWN: 'dm.err.goneWithdrawn',
  // 举报窄通道的两个 409。两条都必须有自己的话：`DM_NOT_READ` 要的是"先打开它"
  //（而那正是一件他做得到的事），`DM_ALREADY_REPORTED` 要的是"你已经交出去了，
  // 而交出不可撤销"——把它们压成一句"操作失败"，第一种会让他放弃，第二种会让他
  // 反复点、以为没生效。
  DM_NOT_READ: 'dm.err.notRead',
  DM_ALREADY_REPORTED: 'dm.err.alreadyReported',
  // 收件人已停用（routes_messages 的 403）。少了这一条，服务端那句"a message to it
  // would never be read"会被丢掉，他打完两千字只拿到一句没有下一步的"发送失败。"，
  // 于是他会重试、换锚点、换格式，最后来问人。写信面另有一道**在他动笔之前**就说
  // 出来的闸（recipientProblem），这一条是那道闸漏过去时的兜底。
  FORBIDDEN: 'dm.err.recipientInactive',
  SUBRESOURCE_BLOCKED: 'dm.err.subresourceBlocked',
};

// 时刻收敛。⚠ `Number(null)` 是 **0**（`Number('')` 也是），所以不能只靠
// Number.isFinite —— 这张行上有六个可空的时刻，任何一个悄悄变成 0 都会在界面上
// 印出 1970-01-01，而那是一句确定的假话（"这封信已经发出去了"）。
// null / undefined / 空串一律给 null。
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// 状态标签键。未知值 ⇒ null（调用侧印原始串）。**不回落到某个已知态**：
// 把一个没见过的状态显示成"已读"是一句确定的假话。
export function stateLabelKey(state) {
  return Object.prototype.hasOwnProperty.call(DM_STATE_LABEL, state)
    ? DM_STATE_LABEL[state] : null;
}
export function stateColor(state) {
  return Object.prototype.hasOwnProperty.call(DM_STATE_COLOR, state)
    ? DM_STATE_COLOR[state] : 'default';
}
export function anchorLabelKey(kind) {
  return Object.prototype.hasOwnProperty.call(DM_ANCHOR_LABEL, kind)
    ? DM_ANCHOR_LABEL[kind] : null;
}
export function failReasonKey(reason) {
  return Object.prototype.hasOwnProperty.call(DM_FAIL_REASON_LABEL, reason)
    ? DM_FAIL_REASON_LABEL[reason] : null;
}

// ── 一行的受控形状 ──────────────────────────────────────────────────────────
// **这里列出的就是全部字段**，没有 body / bodyPreview / snippet / subject / bodyBytes
// / bodyFormat 的位置（头注 1）。id 一律收成字符串：后端给整数、URL 的 focus 参数是
// 字符串、选中态攥在组件手里也是字符串，三处混用 === 是这类页面最经典的静默失配。
export function normalizeMessage(r) {
  if (!r || typeof r !== 'object' || r.id == null) return null;
  return {
    id: String(r.id),
    direction: r.direction === 'out' ? 'out' : 'in',
    counterpartDisplay: typeof r.counterpartDisplay === 'string' ? r.counterpartDisplay : '',
    // SB-150：对方账号邮箱原文——快捷回复预填收件人的**唯一**来源（显示名不是身份，
    // SB-147 病根）。缺席（老后端）给空串：预填出一个空收件人会被 composeProblem
    // 拦住，失败方向是"发不出去"，绝不是"发给一个按显示名猜出来的人"。
    counterpartEmail: typeof r.counterpartEmail === 'string' ? r.counterpartEmail : '',
    anchorKind: typeof r.anchorKind === 'string' ? r.anchorKind : '',
    anchorRef: typeof r.anchorRef === 'string' ? r.anchorRef : '',
    // 标题由服务端按 (kind, ref) 现算（存一份标题快照就会与真相漂开）。空则回落 ref。
    anchorLabel: (typeof r.anchorLabel === 'string' && r.anchorLabel)
      ? r.anchorLabel : (typeof r.anchorRef === 'string' ? r.anchorRef : ''),
    // reply 锚点的**时刻**（服务端不格式化——格式化要先替读者选定时区与语言）。
    // 没有它，一封回信在收件人眼里是「回复你 42 的来信」：一个他在任何界面上都查不到
    // 的裸消息 id（列表面不显示 id），而锚点的全部职责就是给"他凭什么给我写信"的
    // 上下文。选锚点那一侧早就承认了这一点（候选自带 labelMs）。
    anchorLabelMs: num(r.anchorLabelMs),
    // 标题**解出来的语言**（SB-162）：与界面语言不同 ⇒ 服务端没有该语言的译文，
    // 给的是诚实回落的原文。读者要能分清"这条本来就是这个语言写的"与"系统坏了"。
    anchorLabelLang: typeof r.anchorLabelLang === 'string' ? r.anchorLabelLang : null,
    anchorStrong: r.anchorStrong === true,
    state: typeof r.state === 'string' ? r.state : '',
    // ── 第一百二十五批：「已回复」是**与 state 并列的附加事实**，不是第六个状态 ──────────
    // 后端的 dm_state 只有五个值，判定顺序即优先级，且被撤回闸/防 spam/dispatch 三处
    // 消费——那条链上加不得第六个值。而"已读"与"已回复"并不互斥：一封 read 的信可以
    // 被回过、也可以没人回，而后者正是需求方要看见的那一格（谁还欠我回信）。
    // 所以它在这里也是**自己的一位**，绝不去改写 state。缺席（老后端）⇒ null：
    // 少一枚徽标，而不是把一封没人回的信显示成已回复。
    repliedAtMs: num(r.repliedAtMs),
    createdAtMs: num(r.createdAtMs),
    queuedAtMs: num(r.queuedAtMs),
    scheduledAtMs: num(r.scheduledAtMs),
    sentAtMs: num(r.sentAtMs),
    readAtMs: num(r.readAtMs),
    withdrawnAtMs: num(r.withdrawnAtMs),
    failedAtMs: num(r.failedAtMs),
    failReason: typeof r.failReason === 'string' && r.failReason ? r.failReason : null,
    queuedBecauseUnread: r.queuedBecauseUnread === true,
    exposed: r.exposed === true,
    // ── SB-148 B：系统信标记 ────────────────────────────────────────────────
    // 管理员经审过的模板发出的信带 system=1（绕排队，但不绕强读）。读面要让收件人
    // 一眼分辨「这不是某个人手写的」。只认真 true；老后端缺席 ⇒ false——失败方向
    // 是少一枚标记（当普通信呈现），绝不是把一封人写的信冒充成系统信。
    system: r.system === true,
    // ── SB-190 §3.3：「要求响应」标记 ──────────────────────────────────────
    // 发出时即定、事后不补（列 direct_message.response_required 只在 create() 的 INSERT 写）。
    // 只认真 true，老后端缺席 ⇒ false——少一枚徽标，绝不替人标：多标一次就是在对方
    // 头上凭空挂一只钟。
    responseRequired: r.responseRequired === true,
    // ── SB-190 §8 步 5（第二百四十七批）：钟点 ──────────────────────────────
    // 服务端只在「带标记且已发出」的行上给对象（时钟自 sent_at_ms 起，排着队的信没有钟），
    // 其余 null。这里**不按 responseRequired / sentAtMs 自己补算**——那是同一条判据的第二个
    // 实现；也不重算工作日历。形状收敛在 responseSla.mjs（管理页与钟点条共用）。
    responseDeadline: normalizeResponseDeadline(r.responseDeadline),
    // 撤回闸**只认服务端**（头注 2）：这里只把它收成真布尔，绝不自己按 state 重算。
    mayWithdraw: r.mayWithdraw === true,
    // ── 举报窄通道（第一百零九批 P1 ②）─────────────────────────────────────
    // 这两位服务端**只对收件人给**（发送方那一侧恒 null/false，因为「发送方不被通知」）。
    // 这里照收不误、绝不按 direction 自己补算——前端一补，那条裁定就有了第二个实现，
    // 而下一次后端改口径时它会继续按旧口径把事实说出来。
    reportedAtMs: num(r.reportedAtMs),
    mayReport: r.mayReport === true,
    willSendAtMs: num(r.willSendAtMs),
  };
}

export function normalizeMessages(data) {
  const rows = Array.isArray(data?.messages) ? data.messages : [];
  const out = [];
  for (const r of rows) {
    const n = normalizeMessage(r);
    if (n) out.push(n);
  }
  // 刻意**不排序**：顺序（id DESC，新的在前）是后端的展示语义，前端再排一次
  // 就是第二个会漂的顺序。
  return out;
}

// unreadCount：字段缺席（老后端）与"零封未读"必须可分辨——前者给 null（调用侧
// 不渲染徽标），后者给 0（渲染一个 0 就是在说"我确实数过"）。doc_api 的
// updatedAtMs 是同一条教训。
export function unreadCountOf(data) {
  const v = data?.unreadCount;
  return Number.isFinite(Number(v)) ? Math.max(0, Math.trunc(Number(v))) : null;
}

// ── 「这封信什么时候发出」的那句话 ──────────────────────────────────────────
// `willSendAtMs` 是**机器可读**的那一位（契约 C.1）：null 且仍在排队 ⇒ 等对方读上
// 一封。前端不许靠文案猜，所以这只函数是唯一的读法。
// 已发出/终局的信没有"将要发出"这回事 ⇒ null。
export function willSendText(row) {
  if (!row || row.state !== 'queued') return null;
  if (row.willSendAtMs == null) return { key: 'dm.willSend.blocked', ms: null };
  return { key: 'dm.willSend.at', ms: row.willSendAtMs };
}

// ── 曝光留痕（契约 B.7）─────────────────────────────────────────────────────
// **收发双方都要看得见**，两侧字段名与取值完全一致；发送方那一句必须写明"他看得见
// 这一点"，否则曝光就成了一件他不知情的事。时刻取 queuedAtMs（回落 createdAtMs：
// 两者在本设计里恒等，但恒等这件事该由服务端说）。
export function exposureText(row) {
  if (!row || !row.exposed) return null;
  const ms = row.queuedAtMs ?? row.createdAtMs;
  if (ms == null) return null;
  return { key: row.direction === 'in' ? 'dm.exposed.in' : 'dm.exposed.out', ms };
}

// ── 锚点那一行显示什么（契约 C.1 的 labelMs 缝，读取端）────────────────────
// reply 的 ref 是一个裸消息 id，对收件人毫无意义（他在任何界面上都查不到它）；
// 有时刻就用时刻，与选锚点那一侧（anchorOptionLabel）逐字同形。
// 返回 ms 或 null，格式化留给调用侧（它才知道读者的时区与语言）。
export function anchorRefMs(row) {
  return row && row.anchorKind === 'reply' && row.anchorLabelMs != null
    ? row.anchorLabelMs : null;
}

// ── 撤回对话框那句话：**按状态分岔** ────────────────────────────────────────
// 一封 `queued`（从未发出）的信撤回之后是真的无痕；一封**已发出**的信不是——
// 收件箱里那一行**原地留着**（list_inbox 的唯一条件是 sent_at_ms IS NOT NULL，撤回
// 只清正文），他照样看得见「来自 张三 · 关于 … · 已撤回 · 14:03」，点开还会拿到
// 一句"发送方在你打开之前撤回了这封信"。给这两种完全不同的后果同一句话，就是承诺
// 一个做不到的撤回——而那比没有撤回更坏（发送方以为"没发生过"，于是敢在慌张时先发
// 后撤，对方那边留下的却是一条最惹眼的痕迹）。
export function withdrawDescKey(row) {
  return row && row.state === 'sent_unread'
    ? 'dm.withdraw.descSent' : 'dm.withdraw.descQueued';
}

// ── 举报窄通道：已经交出去的那一行说什么 ────────────────────────────────────
// 返回 { key, ms } 或 null。三条判据都在这一只函数里，各自有理由：
//  · **只有收件人看得见这句话**（`direction === 'in'`）。服务端那一侧已经把
//    `reportedAtMs` 对发送方压成了 null，这里再判一次不是重复——它让"发送方不被通知"
//    这条裁定在**前端源码里也读得出来**，而不是一条要靠读后端才知道的隐含约定。
//    退化方向也安全：服务端若哪天回归掉那一位，这一层仍然不显示。
//  · **交出之后就恒显示**，不因为已读/未读或任何别的状态而消失：不可撤销的动作必须
//    在界面上留下一条不会消失的痕迹，否则"我到底交出去没有"这个问题在下一次会话里
//    就没有答案，而他会再点一次（然后拿到 409）。
//  · 没有时刻就不说话：一句"你已经在 — 交出了它"比不说更糟。
export function reportedText(row) {
  if (!row || row.direction !== 'in' || row.reportedAtMs == null) return null;
  return { key: 'dm.report.doneAt', ms: row.reportedAtMs };
}

// ── 第一百二十五批：「已回复」那枚徽标 ──────────────────────────────────────────────
// 返回 { key, ms } 或 null。三件事在这一只函数里定死：
//  · **与状态徽标并排，不替换它**。原状态仍然是 read——"他读了并且回了"与"他读了没回"
//    是两句话，压成一句就把需求方要的那一格弄丢了。调用侧因此渲染**两枚** Chip。
//  · **收发两侧都显示**（不像 reportedText 那样只对收件人说话）：服务端对两侧给同一位
//    同一个值，因为回信本来就躺在对方的收件箱里，这不是秘密。
//  · **没有时刻就不说话**：一句"你已经在 — 回复了"比不说更糟（reportedText 同一条）。
export function repliedText(row) {
  if (!row || row.repliedAtMs == null) return null;
  return { key: 'dm.replied.at', ms: row.repliedAtMs };
}

// 「只看未回复」：**纯前端过滤**，服务端不加参数。
// 它答的是收件箱最常被问的那一句——"谁还欠我回信"。做在前端而不是加一个 query 参数，
// 是因为服务端已经把 `repliedAtMs` 如实给了每一行：再加一个筛选参数就是同一条判据的
// 第二个实现（而那两个实现会在下一次改口径时分叉）。
// ⚠ 不改顺序（顺序是后端的展示语义，见 normalizeMessages 尾注），只做过滤。
export function filterUnreplied(rows, only) {
  const list = Array.isArray(rows) ? rows : [];
  return only ? list.filter((r) => r && r.repliedAtMs == null) : list;
}

// ── 列表窗口：一页取多少行 ──────────────────────────────────────────────────
// 页面拿它发查询，空态文案的口径也拿它判——两处必须是同一个数：手写两遍的下场是
// 改了查询忘了文案，而文案那一半会变成一句**确定的**假话（下面那条）。
export const DM_PAGE_LIMIT = 50;

// 「只看未回复」把清单滤空之后该说哪一句。
// 病灶（第一百二十六批修）：原来恒说 `dm.empty.unreplied`——「这个箱子里的信都已经
// 有回复了」。可这一页只取了 DM_PAGE_LIMIT 行，**滤的是这一页、断言的是整箱**：
// 收件箱超过一页时那句话就是确定的假话，而它偏偏说得最笃定。
// 取满一页 ⇒ 窗口之外还有没有未回的信，本页答不出 ⇒ 只敢把话收到本页。
// `allCount` 是**过滤前**这一页的行数（过滤后的恒为 0，那正是走到这一步的前提）。
// ⚠ `Number(null)` 是 0：null 直接交给 Number 会把「不知道有多少行」误读成「零行」，
// 而零行恰好 < limit ⇒ 替整箱作证。数不出来时一律收到本页（shouldRecheck 头注同一课）。
export function unrepliedEmptyKey(allCount, limit = DM_PAGE_LIMIT) {
  if (typeof allCount !== 'number' || !Number.isFinite(allCount)) return 'dm.empty.unrepliedPage';
  return allCount < limit ? 'dm.empty.unreplied' : 'dm.empty.unrepliedPage';
}

// ── 写信面：**在他动笔之前**就说出来的那道闸 ────────────────────────────────
// 收件人已停用 ⇒ 服务端回 403，而一封发给停用账号的信永远不会被读。锚点端点早就把
// `toAccountActive` 送回来了，写信面此前一处都没读它。返回 i18n 键或 null。
export function recipientProblem(feed) {
  if (!feed) return null;                      // 还没敲定收件人 ⇒ 无话可说
  return feed.toAccountActive === false ? 'dm.new.recipientInactive' : null;
}

// ── 详情要不要先弹"打开就算已读" ────────────────────────────────────────────
// 只有**收件人**打开一封**尚未读**的信才会改变世界（对方收到回执、撤回窗口关死）。
// 自己的发件箱、已读过的信、已撤回/失败的信，点开都不改变任何东西 ⇒ 不弹。
// 一个到处弹的确认框会被训练成"闭着眼点确定"，然后在真要紧的那次也照点。
export function needsReadConfirm(row) {
  return Boolean(row) && row.direction === 'in' && row.state === 'sent_unread';
}

// ── 写信面的可判定校验 ──────────────────────────────────────────────────────
// 返回 i18n 键或 null。顺序即"还差什么"的提示顺序：从上到下按表单的排列，
// 用户改一处就看到下一处，而不是一次抛四条。
// **锚点没有默认值**（见 pickAnchorId）：`needAnchor` 是常态而不是例外，这正是
// 裁定 2 要的——「这封信关于什么」必须由人回答一次。
export function composeProblem(form, nowMs) {
  const to = String(form?.to ?? '').trim();
  if (!to) return 'dm.new.needTo';
  if (!String(form?.anchorId ?? '').trim()) return 'dm.new.needAnchor';
  const body = String(form?.body ?? '');
  if (!body.trim()) return 'dm.new.needBody';
  if (body.length > DM_BODY_MAX) return 'dm.new.tooLong';
  // 延迟发送越界不另造错误键：那句 helper 本身就是"合法窗口是什么"的完整回答
  // （契约 D.4 没有给越界一个键，而临时自造一个键会绕过 D 节的清单）。
  const at = scheduleMs(form?.schedule);
  if (at != null && !scheduleInWindow(at, nowMs)) return 'dm.new.scheduleHelper';
  return null;
}

// `<input type="datetime-local">` 的值 → epoch ms。空串/不可解析 ⇒ null（＝尽快发出）。
// **不做时区转换**：datetime-local 的语义就是"本地墙上时间"，而 new Date(s) 对
// 无时区后缀的串按本地解释——这正是用户在表单里看到的那个时刻。
export function scheduleMs(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : null;
}

// 必须在未来、且不超过 72 小时（契约 C.1 的 400 就是照这两条判的）。
export function scheduleInWindow(atMs, nowMs) {
  const now = Number(nowMs);
  if (!Number.isFinite(atMs) || !Number.isFinite(now)) return false;
  return atMs > now && atMs <= now + DM_SCHEDULE_MAX_MS;
}

// 提交体。**只有服务端认识的字段**：多送一个字段不会报错，只会让下一个读代码的人
// 以为它有用。scheduledAtMs 缺省不送（null 与"不送"在后端同义，而不送更诚实）。
export function composePayload(form) {
  const body = String(form?.body ?? '');
  const { kind, ref } = parseAnchorId(form?.anchorId);
  const payload = {
    toAccount: String(form?.to ?? '').trim(),
    body,
    bodyFormat: form?.bodyFormat === 'plain' ? 'plain' : 'markdown',
    anchorKind: kind,
    anchorRef: ref,
  };
  const at = scheduleMs(form?.schedule);
  if (at != null) payload.scheduledAtMs = at;
  // SB-190 §3.3：只在勾了的时候送 true；不勾不送（同 scheduledAtMs——不送更诚实）。
  // 后端只认 JSON true，所以 1 / 'true' 这类"看着像真"的值在这里就不放行：失败方向
  // 是"没标"，绝不是"替他标了"。
  if (form?.responseRequired === true) payload.responseRequired = true;
  return payload;
}

// ── 撤回失败的三种分码，各自带上"什么时候" ──────────────────────────────────
// 撤回是慌张的时候做的动作，"没撤成"必须说清是哪一种：已读意味着他要去当面解释，
// 已撤回意味着他重复点了，已失败意味着他要重发。一句笼统的"撤不了"让这三件事都做不了。
//
// 时刻从哪来是这只函数存在的理由：**我手里那一行是过期的**——撤回失败恰恰是因为
// 世界在我上一次取数之后变了（他刚读了）。所以优先从服务端报文里取那个 epoch 毫秒
// （后端逐条把它写进了 message），取不到才回落到行上的字段；两处都没有 ⇒ 退回**不带
// 时刻**的那句话，绝不把一个 '—' 塞进句子里（"对方 — 打开了它"比不说更糟）。
export function withdrawFailure(err, row) {
  const spec = {
    ALREADY_READ: ['dm.withdraw.tooLate', 'readAtMs'],
    ALREADY_WITHDRAWN: ['dm.withdraw.already', 'withdrawnAtMs'],
    ALREADY_FAILED: ['dm.withdraw.failed', 'failedAtMs'],
  }[err?.code];
  if (!spec) return null;                       // 调用侧回落到 errKey 的通用表
  const ms = epochMsIn(err?.message) ?? (row ? row[spec[1]] : null) ?? null;
  return ms == null ? { key: DM_ERROR_KEYS[err.code], ms: null } : { key: spec[0], ms };
}

// 报文里那个 epoch 毫秒。只认 12~14 位的整数串：站内信的 id 是几位数，而时刻在本世纪
// 恒是 13 位——收窄到这个宽度，一个带 id 的报文不会被误读成时刻。
export function epochMsIn(message) {
  const m = /\b(\d{12,14})\b/.exec(String(message ?? ''));
  return m ? Number(m[1]) : null;
}

// ── 锚点候选 ────────────────────────────────────────────────────────────────
// 选择器的 value 是 `kind:ref` 一个串（ref 里可能有 `:`，所以只切第一个冒号）。
export function anchorId(a) {
  if (!a || typeof a !== 'object') return '';
  const kind = typeof a.kind === 'string' ? a.kind : '';
  const ref = a.ref == null ? '' : String(a.ref);
  return kind && ref ? `${kind}:${ref}` : '';
}
export function parseAnchorId(id) {
  const s = String(id ?? '');
  const i = s.indexOf(':');
  if (i <= 0) return { kind: '', ref: '' };
  return { kind: s.slice(0, i), ref: s.slice(i + 1) };
}

// same_group **排在最后一位**（风险 6）：「同组」在今天≈「全员」（存量账号默认都在
// MirrorMii），它几乎放行所有人。把它压到底、且不预选，是为了不把裁定 2 的**例外**
// 做成常态——有具体锚点时就该用具体锚点，因为收件人要的是"他凭什么给我写信"这个
// 答案，而"我们都在 MirrorMii"回答不了它。
// 其余项保持后端顺序（work_item → doc → reply）：那是它给的相关性排序。
export function orderAnchors(anchors) {
  const list = Array.isArray(anchors) ? anchors : [];
  const rest = list.filter((a) => a.kind !== 'same_group');
  const same = list.filter((a) => a.kind === 'same_group');
  return [...rest, ...same];
}

export function normalizeAnchorFeed(data) {
  const raw = Array.isArray(data?.anchors) ? data.anchors : [];
  const anchors = [];
  for (const a of raw) {
    if (!a || typeof a !== 'object') continue;
    const id = anchorId(a);
    if (!id) continue;                       // 连身份都没有的候选点不出去
    anchors.push({
      id,
      kind: a.kind,
      ref: String(a.ref),
      // label 可以为空（reply 的 label 恒空——它只给 labelMs，因为「列表不许出现
      // 正文」一样管一个让人挑选的清单面）。调用侧据 kind 拼一句话。
      label: typeof a.label === 'string' ? a.label : '',
      labelMs: num(a.labelMs),
      strong: a.strong === true,
    });
  }
  return {
    toAccount: typeof data?.toAccount === 'string' ? data.toAccount : '',
    toAccountActive: data?.toAccountActive !== false,
    blockedByUnread: data?.blockedByUnread === true,
    blockingMessageId: data?.blockingMessageId == null ? null : String(data.blockingMessageId),
    // 非空 ⇒ 提交必 409。给出来是为了让他**在打完 2000 字之前**就知道。
    pendingMessageId: data?.pendingMessageId == null ? null : String(data.pendingMessageId),
    anchorsTruncated: data?.anchorsTruncated === true,
    anchors: orderAnchors(anchors),
  };
}

// ── SB-169 片②：锚点在**客户端**过滤（需求方 2026-08-22：「搜索文档」基本没法用）──
// 病根不在控件，在两处错位：后端 `q` 匹配的是**内部 docKey**（大小写敏感的子串），
// 而下拉里显示的是 SB-162 本地化**标题**——照着看到的字打进去永远匹配不上；且当时
// 每敲一个键就无防抖地重发整个 anchors 请求（连 work_item/reply/same_group 一起重取）。
// 修法：**取一次、在本地过滤**。匹配面＝label（人看得见的那一行）**与** ref（内部
// 键，知道键的人照样搜得到），不分大小写。
// ⚠ 只过滤**显示**，不改候选集本身：候选集的可见性判定在服务端（那条端点是一台
// 关系预言机，先按我可见过滤再判对方），前端一个字都不许替它判。
export function filterAnchors(anchors, q) {
  const list = Array.isArray(anchors) ? anchors : [];
  const needle = String(q ?? '').trim().toLowerCase();
  if (!needle) return list;
  return list.filter((a) => {
    const label = String(a?.label ?? '').toLowerCase();
    const ref = String(a?.ref ?? '').toLowerCase();
    return label.includes(needle) || ref.includes(needle);
  });
}

// ── SB-169 片①：收件人候选的投影与标签 ──────────────────────────────────────
// 服务端给的是事实位（id / displayName / memberKey / sameGroup），排序与措辞在这里。
// **memberKey 不是邮箱**（OQ-C32 裁决配套约束 (b)：敏感字段仅管理员可见）——它是
// SB-033 的账号 slug，成员面本就可见。SB-147「显示名不是身份」那个病灶由**提交 id**
// 结构性收口（后端 toAccount 兼收 id 与邮箱），不靠让每个人都看见同事的邮箱。
export function normalizeRecipients(data) {
  const raw = Array.isArray(data?.recipients) ? data.recipients : [];
  const out = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object' || r.id == null) continue;
    out.push({
      id: String(r.id),
      displayName: typeof r.displayName === 'string' ? r.displayName : '',
      memberKey: typeof r.memberKey === 'string' ? r.memberKey : '',
      sameGroup: r.sameGroup === true,
    });
  }
  // 同组的排前面（那是"同事"），其余按显示名。组内按显示名——**不按 id**：
  // id 顺序对人没有意义，而人是靠位置记东西的。
  return out.sort((a, b) => (a.sameGroup === b.sameGroup
    ? a.displayName.localeCompare(b.displayName)
    : (a.sameGroup ? -1 : 1)));
}

// 一行显示成什么。区分符优先用成员键；两个人**显示名相同且都没有成员键**时才退到
// `#id`——那是最后一道防线，为的是不让两行长得一模一样（SB-147 的病灶形状），
// 而不是把 id 当作日常称呼摆在所有人面前。
export function recipientOptionLabel(r, all) {
  if (!r || typeof r !== 'object') return '';
  const name = String(r.displayName ?? '');
  if (r.memberKey) return `${name} · ${r.memberKey}`;
  const list = Array.isArray(all) ? all : [];
  const clash = list.some((o) => o && o.id !== r.id && o.displayName === name && !o.memberKey);
  return clash ? `${name} #${r.id}` : name;
}

// 选中的锚点：仍在候选里就留住，不在就**回到未选**（空串）。
// **绝不自动选第一项**，也绝不自动选 same_group：见 orderAnchors 头注。
export function pickAnchorId(anchors, wanted) {
  const list = Array.isArray(anchors) ? anchors : [];
  const w = String(wanted ?? '');
  return list.some((a) => a.id === w) ? w : '';
}

// ── SB-150 快捷回复：详情框里的「回复」──────────────────────────────────────
// ── SB-147：对方那一栏的渲染形——「显示名 <邮箱>」────────────────────────────
// counterpartEmail 自 SB-150 起就在每一行上（通信关系成立即互见，与 OQ-C32 (b)
// 对"全员名录"的收窄不冲突），但读面此前只印显示名——同名两人在列表里不可分辨，
// 而快捷回复恰按邮箱走：人看见的与系统用的不是同一个身份。缺邮箱（部署错峰的
// 旧行）退回只显名；两者皆缺给 '—'（列表不许出现空白单元格）。
export function counterpartLabel(row) {
  const name = typeof row?.counterpartDisplay === 'string' ? row.counterpartDisplay : '';
  const email = typeof row?.counterpartEmail === 'string' ? row.counterpartEmail : '';
  if (name && email) return `${name} <${email}>`;
  return name || email || '—';
}

// 只有**收件方向**且未撤回的信可以从详情框直接回：direction=out 的行回给的是自己；
// withdrawn 的行没有可回的对象（正文已物理清空，拿它作锚也必 400——dm_anchor_check
// 的 reply 支拒撤回信）。这里判的只是**按钮显隐**：锚点方向闸仍由服务端兜底。
export function mayQuickReply(row) {
  return Boolean(row) && row.direction === 'in' && row.state !== 'withdrawn';
}

// 预填形态：收件人=对方、锚点=reply:<本信id>，两者在写信面上锁定不可改。
// **to 必须是 counterpartEmail 而绝不是 counterpartDisplay**（SB-147 病根：显示名
// 不是身份，谁都能把昵称改成"张三"）；缺席给空串——composeProblem 会以 needTo 拦住，
// 失败方向是发不出去，绝不是按显示名解析出一个别人。anchorRef 收成字符串：
// 后端给整数 id，而 anchorId 的 `kind:ref` 拼接与 payload 都要字符串。
export function replyPrefill(row) {
  if (!row || row.id == null) return null;
  return {
    to: typeof row.counterpartEmail === 'string' ? row.counterpartEmail : '',
    anchorKind: 'reply',
    anchorRef: String(row.id),
  };
}

// ── 铃铛深链（风险 5）───────────────────────────────────────────────────────
// 通知只跳到**列表** `/messages?box=inbox&focus={id}`，绝不跳到详情——那会让"点一下
// 铃铛"变成"已读"，而用户以为自己只是看了眼提醒。这两只函数是那条链的读取端。
export function boxFromSearch(search) {
  const v = readParam(search, 'box');
  return v === 'outbox' ? 'outbox' : 'inbox';
}
export function focusIdFromSearch(search) {
  const v = readParam(search, 'focus');
  // 只认十进制数字串：focus 直接参与行高亮的比较，一个任意字符串进来没有害处，
  // 但把它收敛掉能让"这个参数是个 id"成为一件读代码时看得出的事。
  return v && /^\d+$/.test(v) ? v : null;
}
function readParam(search, name) {
  const s = String(search ?? '');
  const q = s.startsWith('?') ? s.slice(1) : s;
  for (const part of q.split('&')) {
    if (!part) continue;
    const i = part.indexOf('=');
    const k = i < 0 ? part : part.slice(0, i);
    if (decodeURIComponent(k) !== name) continue;
    return i < 0 ? '' : decodeURIComponent(part.slice(i + 1).replace(/\+/g, ' '));
  }
  return null;
}
